/**
 * Copying a column.
 *
 * Two questions in one: whether to copy the *values* as well as the column, and
 * then how far along the copy is. The first matters because copying the values
 * of a column on a large table is real work, which is why the backend does it
 * as a job rather than in the request -- and why this stays open to say so
 * rather than firing and forgetting.
 *
 * The copy itself arrives over the websocket as a `field_created`, the way a
 * duplicated application arrives as an `application_created`. Nothing here
 * writes it into the schema cache: the job's answer carries the new field, but
 * a second writer of the same fact is a second thing to keep in step.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { Field, Job } from '@/api/types'
import { duplicateFieldMutation, useJobs } from '@/features/job/queries'
import { isJobFailed, isJobRunning } from '@/features/job/polling'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { Progress } from '@/shared/ui/progress'

export function DuplicateFieldDialog({
  field,
  onClose,
}: {
  field: Field
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [withData, setWithData] = useState(false)
  const [startedId, setStartedId] = useState<number | null>(null)
  const start = useMutation(duplicateFieldMutation(queryClient))

  // The job list is polled while anything in it is running, and stops by
  // itself when nothing is -- so watching one job costs no timer of its own.
  const jobs = useJobs()
  const job = jobs.data?.find((one) => one.id === startedId)

  // Closed on its own once the copy is made: the column is already on screen by
  // then, arriving over the socket, so a dialog still saying "copying" would be
  // the only thing that had not noticed. In an effect rather than in the render
  // it decides in -- closing is the parent's state, and setting a parent's
  // state while rendering a child is React telling you to do it afterwards.
  const done = job !== undefined && !isJobRunning(job) && !isJobFailed(job)
  useEffect(() => {
    if (done) onClose()
  }, [done, onClose])

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('field.duplicate')}</DialogTitle>
          <DialogDescription>{field.name}</DialogDescription>
        </DialogHeader>

        {startedId === null ? (
          <div className="flex items-center gap-2">
            <Checkbox
              id="duplicate-with-data"
              checked={withData}
              onCheckedChange={(checked) => {
                setWithData(checked === true)
              }}
            />
            <Label htmlFor="duplicate-with-data">{t('field.duplicateWithData')}</Label>
          </div>
        ) : (
          <Working job={job} field={field} />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            disabled={startedId !== null}
            onClick={() => {
              start.mutate(
                { fieldId: field.id, withData },
                {
                  onSuccess: (made) => {
                    setStartedId(made.id)
                  },
                },
              )
            }}
          >
            {t('field.duplicate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** How far along the copy is, or what went wrong with it. */
function Working({ job, field }: { job: Job | undefined; field: Field }) {
  const { t } = useTranslation()

  if (job !== undefined && isJobFailed(job)) {
    return (
      <p className="text-sm text-destructive">
        {job.human_readable_error === '' || job.human_readable_error === undefined
          ? t('field.duplicateFailed')
          : job.human_readable_error}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        {t('field.duplicateWorking', { field: field.name })}
      </p>
      {/* Zero until the job list has been asked once, which is a bar at the
          start rather than a bar that is not there. */}
      <Progress value={job?.progress_percentage ?? 0} />
    </div>
  )
}
